import {type Draft, type PayloadAction, createSlice} from '@reduxjs/toolkit';
import {type Locale, MasterLocale} from 'localization/types';
import type {CurrentFilterType} from 'services/advancedSearch/types';
import type {RootState} from 'store/types';
import {
  excludeBokbasenFilter,
  excludeInternationalFilter,
} from 'services/advancedSearch';
import type {DataGridSettings, SettingsState} from './types';

export const initialState: SettingsState = {
  preferredLocale: MasterLocale,
  overview: {
    searchText: '',
    selectedTaskType: 'new',
  },
  datagrid: {},
  advancedSearch: {
    currentFilters: [excludeBokbasenFilter, excludeInternationalFilter],
  },
  showInternational: false,
  mergeSuggestionList: {
    currentType: 'SERIES',
    showUserCreated: false,
  },
  workMinimized: undefined,
};

const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    localeUpdated(state, action: PayloadAction<Locale>) {
      state.preferredLocale = action.payload;
    },
    dataGridSettingsUpdated(
      state,
      action: PayloadAction<{
        name: string;
        settings: DataGridSettings;
      }>,
    ) {
      // Persisted state from before datagrid was introduced may lack the key
      state.datagrid = {
        ...state.datagrid,
        [action.payload.name]: action.payload
          .settings as Draft<DataGridSettings>,
      };
    },
    overviewSelectedTaskTypeUpdated(
      state,
      action: PayloadAction<SettingsState['overview']['selectedTaskType']>,
    ) {
      state.overview.selectedTaskType = action.payload;
    },
    overviewSearchTextUpdated(
      state,
      action: PayloadAction<SettingsState['overview']['searchText']>,
    ) {
      state.overview.searchText = action.payload;
    },
    advancedSearchFiltersUpdated(
      state,
      action: PayloadAction<CurrentFilterType[]>,
    ) {
      state.advancedSearch.currentFilters = action.payload;
    },
    showInternationalUpdated(
      state,
      action: PayloadAction<SettingsState['showInternational']>,
    ) {
      state.showInternational = action.payload;
    },
    mergeSuggestionTypeUpdated(
      state,
      action: PayloadAction<
        SettingsState['mergeSuggestionList']['currentType']
      >,
    ) {
      state.mergeSuggestionList.currentType = action.payload;
    },
    mergeSuggestionShowUserCreatedUpdated(
      state,
      action: PayloadAction<
        SettingsState['mergeSuggestionList']['showUserCreated']
      >,
    ) {
      state.mergeSuggestionList.showUserCreated = action.payload;
    },
    workMinimizedUpdated(
      state,
      action: PayloadAction<SettingsState['workMinimized']>,
    ) {
      state.workMinimized = action.payload;
    },
  },
});

export const {
  localeUpdated,
  dataGridSettingsUpdated,
  overviewSelectedTaskTypeUpdated,
  overviewSearchTextUpdated,
  advancedSearchFiltersUpdated,
  showInternationalUpdated,
  mergeSuggestionTypeUpdated,
  mergeSuggestionShowUserCreatedUpdated,
  workMinimizedUpdated,
} = settingsSlice.actions;

export const selectSettingsPreferredLocale = (state: RootState) =>
  state.settings.preferredLocale;
export const selectSettingsAdvancedSearchCurrentFilters = (state: RootState) =>
  state.settings.advancedSearch.currentFilters;

export const selectSettingsShowInternational = (state: RootState) =>
  state.settings.showInternational;

// ======= DATAGRID =======
export const selectSettingsDataGrid = (
  state: RootState,
  name: string,
): DataGridSettings | undefined => state.settings.datagrid?.[name];

// ======= OVERVIEW =======
export const selectSettingsOverviewSelectedTaskType = (state: RootState) =>
  state.settings.overview.selectedTaskType;
export const selectSettingsOverviewSearchText = (state: RootState) =>
  state.settings.overview.searchText;

// ======= MERGE SUGGESTIONS =======
export const selectMergeSuggestionCurrentType = (state: RootState) =>
  state.settings.mergeSuggestionList.currentType;
export const selectMergeSuggestionShowUserCreated = (state: RootState) =>
  state.settings.mergeSuggestionList.showUserCreated;

// ======= METADATA =======
export const selectWorkMinimized = (state: RootState) =>
  state.settings.workMinimized;

export default settingsSlice;
